import { useMemo } from "react";
import { CheckCircle2, Clock3, Timer } from "lucide-react";
import { Modal } from "@/components/ui/Modal";
import {
  CHAMADO_STATUS,
  type ChamadoDto,
  type ChamadoStatus,
} from "@/services/chamados";
import { cn } from "@/utils/cn";
import { formatDateTime } from "@/utils/format";

interface Props {
  chamado: ChamadoDto | null;
  open: boolean;
  onClose: () => void;
}

const STATUS_LABELS: Record<ChamadoStatus, string> = {
  esperando_analise: "Esperando Análise",
  em_analise: "Em Análise",
  corrigindo: "Corrigindo",
  esperando_validacao: "Esperando Validação",
  finalizada: "Finalizada",
};

const STATUS_COLORS: Record<ChamadoStatus, { dot: string; bar: string; badge: string }> = {
  esperando_analise: {
    dot: "bg-amber-400",
    bar: "bg-amber-400",
    badge: "border-amber-200 bg-amber-50 text-amber-800",
  },
  em_analise: {
    dot: "bg-blue-400",
    bar: "bg-blue-400",
    badge: "border-blue-200 bg-blue-50 text-blue-800",
  },
  corrigindo: {
    dot: "bg-violet-400",
    bar: "bg-violet-400",
    badge: "border-violet-200 bg-violet-50 text-violet-800",
  },
  esperando_validacao: {
    dot: "bg-orange-400",
    bar: "bg-orange-400",
    badge: "border-orange-200 bg-orange-50 text-orange-800",
  },
  finalizada: {
    dot: "bg-emerald-400",
    bar: "bg-emerald-400",
    badge: "border-emerald-200 bg-emerald-50 text-emerald-800",
  },
};

interface Segmento {
  status: ChamadoStatus;
  inicioMs: number;
  /** null = etapa atual, ainda em andamento. */
  fimMs: number | null;
}

function isChamadoStatus(value: string): value is ChamadoStatus {
  return (CHAMADO_STATUS as readonly string[]).includes(value);
}

/**
 * Reconstrói os períodos em cada etapa a partir do histórico
 * (entradas "criado" e "status_alterado", cuja descrição codifica "de X para Y").
 */
function computeSegmentos(chamado: ChamadoDto): Segmento[] {
  const eventos = (chamado.historico ?? [])
    .filter((entry) => entry.acao === "status_alterado")
    .slice()
    .sort((a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime());

  const segmentos: Segmento[] = [];
  let statusAtual: ChamadoStatus = "esperando_analise";
  let inicioAtual = new Date(chamado.createdAt).getTime();

  for (const evento of eventos) {
    const match = /de (\S+) para (\S+)\./.exec(evento.descricao);
    const destino = match?.[2];
    if (!destino || !isChamadoStatus(destino)) continue;
    const momento = new Date(evento.createdAt).getTime();
    segmentos.push({ status: statusAtual, inicioMs: inicioAtual, fimMs: momento });
    statusAtual = destino;
    inicioAtual = momento;
  }

  segmentos.push({ status: statusAtual, inicioMs: inicioAtual, fimMs: null });
  return segmentos;
}

function formatDuracao(ms: number): string {
  const minutes = Math.floor(Math.max(0, ms) / 60_000);
  if (minutes < 1) return "< 1 min";
  if (minutes < 60) return `${minutes} min`;
  const hours = Math.floor(minutes / 60);
  const remMin = minutes % 60;
  if (hours < 24) return remMin > 0 ? `${hours}h ${remMin}min` : `${hours}h`;
  const days = Math.floor(hours / 24);
  const remHours = hours % 24;
  return remHours > 0 ? `${days}d ${remHours}h` : `${days}d`;
}

export function ChamadoLeadTimeModal({ chamado, open, onClose }: Props) {
  const dados = useMemo(() => {
    if (!chamado) return null;
    const nowMs = Date.now();
    const segmentos = computeSegmentos(chamado);
    const finalizado = chamado.status === "finalizada";

    // Lead time encerra ao entrar em "finalizada"; tempo após a conclusão não conta.
    const duracaoDoSegmento = (segmento: Segmento): number => {
      if (segmento.fimMs != null) return segmento.fimMs - segmento.inicioMs;
      if (segmento.status === "finalizada") return 0;
      return nowMs - segmento.inicioMs;
    };

    const porStatus = new Map<ChamadoStatus, number>();
    for (const segmento of segmentos) {
      const atual = porStatus.get(segmento.status) ?? 0;
      porStatus.set(segmento.status, atual + duracaoDoSegmento(segmento));
    }

    const inicioMs = new Date(chamado.createdAt).getTime();
    const ultimoSegmento = segmentos[segmentos.length - 1];
    const fimMs = finalizado ? ultimoSegmento.inicioMs : nowMs;
    const totalMs = Math.max(0, fimMs - inicioMs);

    const breakdown = CHAMADO_STATUS
      .map((status) => ({
        status,
        duracaoMs: porStatus.get(status) ?? 0,
        percentual: totalMs > 0 ? ((porStatus.get(status) ?? 0) / totalMs) * 100 : 0,
      }))
      .filter((item) => item.status !== "finalizada" || item.duracaoMs > 0);

    return { segmentos, breakdown, totalMs, inicioMs, fimMs, finalizado, nowMs };
  }, [chamado]);

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={
        <div className="flex min-w-0 items-center gap-2">
          <Timer className="h-5 w-5 shrink-0 text-brand-600" />
          <span className="truncate text-base font-semibold text-slate-800">
            Lead time — {chamado?.titulo ?? "Chamado"}
          </span>
        </div>
      }
      size="md"
      closeOnEscape
      closeOnBackdropClick
    >
      {chamado && dados && (
        <div className="space-y-5">
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
            <div className="rounded-xl border border-slate-200 bg-slate-50 px-4 py-3">
              <p className="text-xs font-medium text-slate-500">Criado em</p>
              <p className="mt-1 text-sm font-semibold text-slate-800">
                {formatDateTime(chamado.createdAt)}
              </p>
            </div>
            <div className="rounded-xl border border-slate-200 bg-slate-50 px-4 py-3">
              <p className="text-xs font-medium text-slate-500">
                {dados.finalizado ? "Finalizado em" : "Situação"}
              </p>
              <p className="mt-1 flex items-center gap-1.5 text-sm font-semibold text-slate-800">
                {dados.finalizado ? (
                  <>
                    <CheckCircle2 className="h-4 w-4 shrink-0 text-emerald-500" />
                    {formatDateTime(new Date(dados.fimMs).toISOString())}
                  </>
                ) : (
                  <>
                    <Clock3 className="h-4 w-4 shrink-0 text-amber-500" />
                    Em andamento
                  </>
                )}
              </p>
            </div>
            <div className="rounded-xl border border-brand-200 bg-brand-50 px-4 py-3">
              <p className="text-xs font-medium text-brand-700">Lead time total</p>
              <p className="mt-1 text-sm font-bold text-brand-800">
                {formatDuracao(dados.totalMs)}
              </p>
            </div>
          </div>

          <div>
            <h3 className="mb-2 text-xs font-bold uppercase tracking-wide text-slate-500">
              Tempo por etapa
            </h3>
            <div className="flex h-3 w-full overflow-hidden rounded-full bg-slate-100">
              {dados.breakdown
                .filter((item) => item.percentual > 0)
                .map((item) => (
                  <div
                    key={item.status}
                    className={cn("h-full", STATUS_COLORS[item.status].bar)}
                    style={{ width: `${item.percentual}%` }}
                    title={`${STATUS_LABELS[item.status]}: ${formatDuracao(item.duracaoMs)}`}
                  />
                ))}
            </div>
            <ul className="mt-3 space-y-1.5">
              {dados.breakdown.map((item) => (
                <li
                  key={item.status}
                  className="flex items-center justify-between gap-2 rounded-lg border border-slate-100 bg-white px-3 py-2 text-sm"
                >
                  <span className="flex items-center gap-2 text-slate-700">
                    <span
                      className={cn("h-2.5 w-2.5 rounded-full", STATUS_COLORS[item.status].dot)}
                    />
                    {STATUS_LABELS[item.status]}
                  </span>
                  <span className="flex items-center gap-2">
                    <span className="font-semibold text-slate-800">
                      {formatDuracao(item.duracaoMs)}
                    </span>
                    <span className="w-12 text-right text-xs text-slate-400">
                      {item.percentual.toFixed(0)}%
                    </span>
                  </span>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h3 className="mb-2 text-xs font-bold uppercase tracking-wide text-slate-500">
              Linha do tempo
            </h3>
            <ol className="space-y-0">
              {dados.segmentos.map((segmento, index) => {
                const emAndamento = segmento.fimMs == null && segmento.status !== "finalizada";
                const duracaoMs =
                  segmento.fimMs != null
                    ? segmento.fimMs - segmento.inicioMs
                    : segmento.status === "finalizada"
                      ? null
                      : dados.nowMs - segmento.inicioMs;
                return (
                  <li key={`${segmento.status}-${segmento.inicioMs}`} className="flex gap-3">
                    <div className="flex flex-col items-center">
                      <span
                        className={cn(
                          "mt-1 h-2.5 w-2.5 shrink-0 rounded-full",
                          STATUS_COLORS[segmento.status].dot,
                        )}
                      />
                      {index < dados.segmentos.length - 1 && (
                        <span className="w-px flex-1 bg-slate-200" />
                      )}
                    </div>
                    <div className="min-w-0 flex-1 pb-4">
                      <div className="flex flex-wrap items-center gap-2">
                        <span
                          className={cn(
                            "rounded-full border px-2 py-0.5 text-xs font-semibold",
                            STATUS_COLORS[segmento.status].badge,
                          )}
                        >
                          {STATUS_LABELS[segmento.status]}
                        </span>
                        {duracaoMs != null && (
                          <span className="text-xs font-medium text-slate-500">
                            {formatDuracao(duracaoMs)}
                            {emAndamento && " (em andamento)"}
                          </span>
                        )}
                      </div>
                      <p className="mt-1 text-xs text-slate-400">
                        {formatDateTime(new Date(segmento.inicioMs).toISOString())}
                        {segmento.fimMs != null &&
                          ` → ${formatDateTime(new Date(segmento.fimMs).toISOString())}`}
                      </p>
                    </div>
                  </li>
                );
              })}
            </ol>
          </div>
        </div>
      )}
    </Modal>
  );
}
